import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray } from '@angular/forms';
import { SyllabusQuery } from '@examdojo/category';
import { distinctUntilDeepChanged, shareOneReplay } from '@examdojo/core/rxjs';
import { isNullish } from '@examdojo/core/util/nullish';
import { QuestionItemsFormService, TopLevelFormGroup } from '@examdojo/question';
import { ReferenceQuestionQuery, ReferenceQuestionStoreModel } from '@examdojo/reference-question';
import { QuestionType, ReferenceQuestionReviewType } from '@examdojo/review';
import { BehaviorSubject, combineLatest, from, map, merge, Observable, of, startWith, switchMap } from 'rxjs';
import { QuestionItemsCachingService } from '../question-items-caching/question-items-caching.service';
import {
  QuestionEditorCachingConfig,
  QuestionEditorCombinedState,
} from '../question-items-caching/question-items-caching.types';
import { getReferenceQuestionReviewConfig } from './ref-question-validation.model';

@Injectable()
export class ReferenceQuestionEditorItemsFormService extends QuestionItemsFormService {
  constructor(
    private readonly referenceQuestionQuery: ReferenceQuestionQuery,
    private readonly questionItemsCachingService: QuestionItemsCachingService,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    super();

    this.setupAutoSave();
  }

  readonly form = new FormArray<TopLevelFormGroup>([]);

  readonly mode$$ = new BehaviorSubject<ReferenceQuestionReviewType | null>(null);

  readonly combinedState$: Observable<QuestionEditorCombinedState | null> = combineLatest({
    questionId: this.referenceQuestionQuery.selectActiveId(),
    mode: this.mode$$,
    syllabus: this.syllabusQuery.active$,
    editingOptions: this.mode$$.pipe(map((mode) => mode && getReferenceQuestionReviewConfig(mode))),
  }).pipe(
    switchMap((state) => {
      const { mode, syllabus, editingOptions, questionId } = state;
      if (!mode || !syllabus || !editingOptions || !questionId) {
        return of(null);
      }

      const questionEditorCachingConfig: QuestionEditorCachingConfig = {
        ...state,
        mode,
        questionId,
        syllabusId: syllabus.id,
        questionType: QuestionType.Reference,
        markCalculationMethod: syllabus.mark_calculation_method ?? 'ADDITIVE',
        freshQuestionItems: this.getFreshQuestionItems(questionId),
      };

      return merge(
        of(null),
        from(this.loadQuestionItems(questionEditorCachingConfig)).pipe(
          map((questionItems) => ({
            ...questionEditorCachingConfig,
            questionItems,
            editingOptions,
          })),
        ),
      );
    }),
    startWith(null),
    distinctUntilDeepChanged(),
    shareOneReplay(),
  );

  readonly editingOptions$ = this.combinedState$.pipe(map((state) => state?.editingOptions ?? null));
  readonly questionItems$ = this.combinedState$.pipe(map((state) => state?.questionItems ?? null));

  override readonly generateSolutionQuestionId$ = this.referenceQuestionQuery.selectActiveId();

  private async loadQuestionItems(config: QuestionEditorCachingConfig) {
    if (isNullish(config.questionId)) {
      return null;
    }

    try {
      return await this.questionItemsCachingService.loadQuestionItemsWithCaching(config);
    } catch (error) {
      return this.questionItemsCachingService.handleLoadError(error, () =>
        this.getFreshQuestionItems(config.questionId),
      );
    }
  }

  private getFreshQuestionItems(id: ReferenceQuestionStoreModel['id']) {
    if (isNullish(id)) {
      return null;
    }

    return this.referenceQuestionQuery.getUIEntity(id)?.question_items ?? null;
  }

  private setupAutoSave() {
    this.questionItemsCachingService
      .setupAutoSave(this.combinedState$, this.currentState$)
      .pipe(takeUntilDestroyed())
      .subscribe();
  }
}
